import React, { useState, useEffect, useRef } from 'react'
import { Select, Tooltip } from 'antd'
import {
  getConnectedDevices,
  setUserMedia,
  removeDisplayMedia,
} from '../utils/mediaDevices'
import Main from './Main'
import { useVideoStore } from '../VideoSetting'
import classNames from 'classnames'
import VolumeMeter from '../components/VolumeMeter'
import useResizeObserver from 'use-resize-observer'
import { MD, SM } from '../breakpoints'
import { Alert } from 'antd'

type Props = {
  server: string
  videoId: string
  room: string
  pin: string
  token: string
}

const Janus: React.FC<Props> = ({ server, videoId, room, pin, token }) => {
  const { ref, width = 0, height = 0 } = useResizeObserver<HTMLDivElement>()

  const [joined, setJoined] = useState<boolean>(false)
  const [loading, setloading] = useState<boolean>(false)
  const [audioDevices, setAudioDevices] = useState<any>([])
  const [videoDevices, setVideoDevices] = useState<any>([])

  const videoRef = useRef<any>()
  const canvasRef = useRef<any>()
  const userMedia = useRef<any>(null)
  const audioDev = useRef<any>('')
  const videoDev = useRef<any>('')
  const joinedRef = useRef<any>(joined)

  joinedRef.current = joined

  const type = token.includes('at-') ? 'agent' : 'client'

  const { Option } = Select

  const {
    audio,
    video,
    webcamAccess,
    audioAccess,
    small,
    status,
    errorMedia,
    setAudio,
    setVideo,
  } = useVideoStore(state => ({
    audio: state.audio,
    video: state.video,
    webcamAccess: state.webcamAccess,
    audioAccess: state.audioAccess,
    small: state.small,
    status: state.status,
    errorMedia: state.errorMedia,
    setAudio: state.setAudio,
    setVideo: state.setVideo,
  }))

  useEffect(() => {
    navigator.mediaDevices.addEventListener('devicechange', async event => {
      if (!joinedRef.current) {
        setloading(true)
        await removeDisplayMedia(userMedia.current)
        setAudioDevices([])
        setVideoDevices([])
        getDevices()
      }
    })

    getDevices()
  }, [])

  useEffect(() => {
    if (videoRef.current) {
      const videWidth = 630
      const videHeight = 473

      if (width < MD && width > SM) {
        videoRef.current.height = videHeight / 1.5
        videoRef.current.width = videWidth / 1.5
      } else if (width < SM) {
        videoRef.current.height = videHeight / 2
        videoRef.current.width = videWidth / 2
      } else {
        videoRef.current.height = videHeight
        videoRef.current.width = videWidth
      }
    }
  }, [width])

  const setVideoPlayer = () => {
    if ('srcObject' in videoRef.current) {
      videoRef.current.srcObject = userMedia.current
    } else {
      videoRef.current.src = window.URL.createObjectURL(userMedia.current)
    }

    setloading(false)
  }

  const getDevices = async () => {
    userMedia.current = await setUserMedia()

    if ('srcObject' in videoRef.current) {
      videoRef.current.srcObject = userMedia.current
    } else {
      videoRef.current.src = window.URL.createObjectURL(userMedia.current)
    }

    videoRef.current.onloadedmetadata = e => {
      videoRef.current.play()
    }

    videoRef.current.onplaying = () => {
      canvasRef.current.height = videoRef.current.videoHeight
      canvasRef.current.width = videoRef.current.videoWidth
    }

    setloading(false)

    const audioinput = await getConnectedDevices('audioinput')
    const videoinput = await getConnectedDevices('videoinput')

    if (audioinput[0] && audioinput[0].deviceId) {
      setAudioDevices(audioinput)
      audioDev.current = audioinput[0].deviceId
    }

    if (videoinput[0] && videoinput[0].deviceId) {
      setVideoDevices(videoinput)
      videoDev.current = videoinput[0].deviceId
    }
  }

  const handleChangeAudio = async value => {
    audioDev.current = await value

    setloading(true)

    await removeDisplayMedia(userMedia.current)

    userMedia.current = await setUserMedia(audioDev.current, videoDev.current)

    setVideoPlayer()
  }

  const handleChangeVideo = async value => {
    videoDev.current = await value

    setloading(true)

    await removeDisplayMedia(userMedia.current)

    userMedia.current = await setUserMedia(audioDev.current, videoDev.current)

    setVideoPlayer()
  }

  const join = async () => {
    setJoined(true)
  }

  const mute = (kind: string) => {
    if (kind == 'video') {
      setVideo(!video)
    } else {
      setAudio(!audio)
    }
  }

  return (
    <div className={classNames('janus', { 'janus--small': small })} ref={ref}>
      {joined ? (
        <Main
          server={server}
          videoId={videoId}
          room={parseInt(room)}
          pin={pin}
          token={token}
          type={type}
          userMedia={userMedia}
        />
      ) : (
        <div className="janus__join">
          <div className="janus__join-select">
            <div>
              <h3 className="janus__title">
                {intl.formatMessage({ id: 'janus.select.audio.title' })}:
              </h3>
              {audioDevices[0] ? (
                <Select
                  onChange={handleChangeAudio}
                  defaultValue={audioDevices[0].deviceId}
                >
                  {audioDevices.map(item => (
                    <Option key={item.deviceId} value={item.deviceId}>
                      {item.label}
                    </Option>
                  ))}
                </Select>
              ) : (
                <>
                  <Select defaultValue="1" disabled>
                    <Option value="1">
                      {intl.formatMessage({
                        id: 'janus.select.audio.noAccess',
                      })}
                    </Option>
                  </Select>
                </>
              )}
            </div>
            <div>
              <h3 className="janus__title">
                {intl.formatMessage({ id: 'janus.select.video.title' })}:
              </h3>
              {videoDevices[0] ? (
                <Select
                  onChange={handleChangeVideo}
                  defaultValue={videoDevices[0].deviceId}
                >
                  {videoDevices.map(item => (
                    <Option key={item.deviceId} value={item.deviceId}>
                      {item.label}
                    </Option>
                  ))}
                </Select>
              ) : (
                <>
                  <Select defaultValue="1" disabled>
                    <Option value="1">
                      {intl.formatMessage({
                        id: 'janus.select.video.noAccess',
                      })}
                    </Option>
                  </Select>
                </>
              )}
            </div>
          </div>
          <div className="janus__volumemeter">
            {!loading && userMedia.current && (
              <VolumeMeter userMedia={userMedia} />
            )}
          </div>
          <div
            className={classNames('janus__join-test', {
              'janus__join-test--disabled': !video,
            })}
          >
            <video
              width="630"
              height="473"
              ref={videoRef}
              id="testvideo"
              autoPlay
              playsInline
              muted
            />
            <canvas className="video-canvas" hidden ref={canvasRef}></canvas>
            <div className="janus__join-settings">
              {audioAccess ? (
                <Tooltip
                  title={
                    audio && audioAccess
                      ? intl.formatMessage({ id: 'janus.button.audio.off' })
                      : intl.formatMessage({ id: 'janus.button.audio.on' })
                  }
                >
                  <button
                    onClick={() => mute('audio')}
                    className={classNames('btn janus__button', {
                      'janus__button--off': !audio,
                    })}
                  >
                    {audio && audioAccess ? (
                      <i className="fas fa-microphone"></i>
                    ) : (
                      <i className="fas fa-microphone-slash"></i>
                    )}{' '}
                  </button>
                </Tooltip>
              ) : (
                <button className="btn janus__button" disabled>
                  <i className="fas fa-microphone-slash"></i>
                </button>
              )}
              {webcamAccess ? (
                <Tooltip
                  title={
                    video && webcamAccess
                      ? intl.formatMessage({ id: 'janus.button.video.off' })
                      : intl.formatMessage({ id: 'janus.button.video.on' })
                  }
                >
                  <button
                    onClick={() => mute('video')}
                    className={classNames('btn janus__button', {
                      'janus__button--off': !video,
                    })}
                  >
                    {video && webcamAccess ? (
                      <i className="fas fa-video"></i>
                    ) : (
                      <i className="fas fa-video-slash"></i>
                    )}
                  </button>
                </Tooltip>
              ) : (
                <button className="btn janus__button" disabled>
                  <i className="fas fa-video-slash"></i>
                </button>
              )}
            </div>
          </div>
          <div>
            <button
              type="button"
              disabled={status != 'success'}
              className="btn janus__join-button"
              onClick={() => join()}
            >
              {intl.formatMessage({ id: 'janus.button.join' })}
            </button>
          </div>
          {status == 'waiting' && (
            <div className="janus__join-alert">
              <Alert
                message={intl.formatMessage({
                  id: 'mic perm / waiting',
                })}
                type="warning"
                showIcon
              />
            </div>
          )}
          {errorMedia && (
            <div className="janus__join-alert">
              <Alert
                message={(() => {
                  switch (errorMedia) {
                    case 'AbortError':
                      return intl.formatMessage({
                        id: 'mic perm / fail / AbortError',
                      })
                    case 'NotAllowedError':
                      return intl.formatMessage({
                        id: 'mic perm / fail / NotAllowedError',
                      })
                    case 'NotFoundError':
                      return intl.formatMessage({
                        id: 'mic perm / fail / NotFoundError',
                      })
                    case 'NotReadableError':
                      return intl.formatMessage({
                        id: 'mic perm / fail / NotReadableError',
                      })
                    case 'OverconstrainedError':
                      return intl.formatMessage({
                        id: 'mic perm / fail / OverconstrainedError',
                      })
                    case 'SecurityError':
                      return intl.formatMessage({
                        id: 'mic perm / fail / SecurityError',
                      })
                  }
                })()}
                type="error"
                showIcon
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Janus
